import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const SERVICES = [
  {
    eyebrow: "01",
    title: "Retirement Planning",
    available: true,
    description:
      "Retirement planning under federal Enrolled Agent licensure: Roth conversions, Required Minimum Distributions, the taxation of Social Security benefits, and the tax treatment of distributions from qualified plans. Engagements focus on the planning and tax aspects of retirement and do not include investment-adviser services or the recommendation of specific securities.",
    bullets: [
      "Multi-year Roth-conversion modeling and bracket management",
      "Required Minimum Distribution (RMD) timing & taxation",
      "Social Security benefit taxation analysis (provisional-income review)",
      "Tax treatment of 401(k), 403(b), IRA, and pension distributions",
      "Net Unrealized Appreciation (NUA) tax review for employer-stock holdings",
      "Coordination with your investment professional on the tax side of decisions",
    ],
  },
  {
    eyebrow: "02",
    title: "Individual Tax Preparation",
    available: true,
    description:
      "Federal and multi-state Form 1040 preparation for W-2 earners, freelancers, gig workers, and families. Every return is reviewed line-by-line and explained in plain English so you understand what was filed and why.",
    bullets: [
      "Federal and multi-state returns (1040, schedules, state forms)",
      "Self-employment income (Schedule C) and 1099-K reconciliation",
      "Rental property and investment income reporting",
      "ITIN applications & renewals",
      "E-filing, secure document exchange, and year-round questions",
    ],
  },
  {
    eyebrow: "03",
    title: "Small-Business Tax Planning",
    available: true,
    description:
      "Year-round tax planning for sole proprietors, single-member LLCs, S-corporations, and partnerships. We model the tax impact of business decisions before you make them — quarterly, not just at filing time.",
    bullets: [
      "Entity selection consultation (Sole Prop / LLC / S-Corp)",
      "Quarterly estimated-tax planning",
      "Reasonable-compensation analysis for S-corp owners",
      "Form 1120-S, 1065, and Schedule K-1 preparation",
      "Bookkeeping cleanup referrals & coordination",
    ],
  },
  {
    eyebrow: "04",
    title: "IRS Representation & Resolution",
    available: true,
    description:
      "As a federally licensed Enrolled Agent, the firm is authorized to represent taxpayers before all administrative levels of the Internal Revenue Service — in any state, on any tax matter.",
    bullets: [
      "IRS audit representation (correspondence, office, and field)",
      "Notices, CP2000 responses, and amended returns",
      "Installment agreements and currently-not-collectible status",
      "Offers in Compromise (where appropriate)",
      "Penalty abatement and collection appeals",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="page-services" className="bg-[#F9F8F6]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-6">— Services</div>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#0F292F] font-light tracking-tight max-w-4xl leading-[1.05]">
          A <span className="italic text-[#8E6A2B]">financial planning</span> firm.
        </h1>
        <p className="mt-8 text-lg text-[#52525B] max-w-2xl leading-relaxed">
          H. Elliot Associates is a financial planning firm specializing in two disciplines:
          retirement planning and tax planning. Investment-adviser services are not currently
          offered.
        </p>
      </section>

      <div className="hairline max-w-7xl mx-auto" />

      {SERVICES.map((s, i) => (
        <section key={s.title} className={i % 2 === 0 ? "bg-white" : "bg-[#F3F1EC]"}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="font-serif text-7xl md:text-8xl font-light text-[#8E6A2B]/40 leading-none">{s.eyebrow}</div>
            </div>
            <div className="lg:col-span-8">
              <h2 className="font-serif text-4xl md:text-5xl text-[#0F292F] font-light leading-tight mb-6">
                {s.title}
              </h2>
              <p className="text-lg text-[#52525B] leading-relaxed mb-10 max-w-2xl">{s.description}</p>
              <ul className="space-y-4 max-w-xl">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-4 text-[#1A1A1A]">
                    <span className="w-6 h-px bg-[#8E6A2B] mt-3 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}

      <section className="bg-[#0F292F] text-white py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-light leading-tight">
            Curious whether we can help?
          </h2>
          <p className="mt-6 text-white/70 text-lg max-w-2xl mx-auto">
            A 30-minute introductory consultation, at no cost — for tax, retirement, or both.
            We'll review your situation, tell you honestly whether we're the right fit, and
            outline what your engagement might look like.
          </p>
          <div className="mt-10">
            <Link to="/book" data-testid="services-book-btn" className="btn-accent">
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
